import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { combineAll } from '@datachain/ui-sdk/common';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, debounceTime, map, switchMap } from 'rxjs';

import { ProjectMemberRequester } from '../../requesters/core/project-member.requester';
import { ProjectRequester } from '../../requesters/core/project-requester';
import { OrphanProjectParser } from '../orphan-project-parser';
import {
	initOrphanProjectsList,
	fetchOrphanProjectsFail,
	fetchOrphanProjectsSuccess,
	setOrphanProjectAdmin,
	refreshOrphanProjectsList,
	assignOrphanProjectsAdmins,
} from './orphan-project.actions';

@Injectable()
export class OrphanProjectEffects {
	public orphanProjects$ = createEffect(() =>
		this.action$.pipe(
			ofType(initOrphanProjectsList, refreshOrphanProjectsList),
			switchMap(() =>
				combineAll({
					orphanProjects: this.projectRequester.getOrphanProjects(),
					allProjectsMembers:
						this.userRequester.getAvailableMembersFromAllProjects(),
				})
			),
			map((res) => {
				const orphanProjectList = this.orphanProjectParser.getOrphanProjectList(
					res.orphanProjects
				);
				const admins = this.orphanProjectParser.parseAdmins(
					res.allProjectsMembers
				);
				return fetchOrphanProjectsSuccess({
					orphanProjectList,
					admins,
				});
			}),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					fetchOrphanProjectsFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public assignAdmins$ = createEffect(() =>
		this.action$.pipe(
			ofType(assignOrphanProjectsAdmins),
			map((action) =>
				action.projectAdmin.forEach((v, k) => {
					this.store.dispatch(
						setOrphanProjectAdmin({
							id: k,
							admin: v,
						})
					);
				})
			),
			debounceTime(320),
			map(() => refreshOrphanProjectsList()),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					fetchOrphanProjectsFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public setAdmin$ = createEffect(
		() =>
			this.action$.pipe(
				ofType(setOrphanProjectAdmin),
				switchMap((action) =>
					this.projectRequester.setOrphanProjectAdmins(action.id, [
						action.admin,
					])
				),
				catchError((error: HttpErrorResponse, caught) => {
					console.error(error);
					this.store.dispatch(
						fetchOrphanProjectsFail({
							error,
						})
					);
					return caught;
				})
			),
		{
			dispatch: false,
		}
	);

	public constructor(
		private readonly action$: Actions,
		private readonly projectRequester: ProjectRequester,
		private readonly userRequester: ProjectMemberRequester,
		private readonly store: Store,
		private readonly orphanProjectParser: OrphanProjectParser
	) {}
}
